/**
 * What the renderer draws of the power-ups' world-wide effects (spec 05; the ITEMS stream's view).
 * Positions are in the VIC frame the items use (8 x tile + $14, 8 x tile + $2D for a 2x2 item's box).
 */

import { blastPieces, blastProgress, type BlastPiece } from './blast';
import { strikeBox } from './strike';
import { BOMB_X, BONUS_PICTURES, LAMP_X, LAMP_Y } from './tuning';
import type { ItemSystem } from './state';
import type { World } from '../types';

/** The potion bonus stage as shown: its items are `world.items` of kind 'bonus'. */
export interface BonusLook {
  phase: 'draw' | 'run' | 'results';
  /** The stage's real picture (item graphics 63 / 65 / 64; `Item.item` holds a diamond stand-in) and C64 colour. */
  picture: 'note' | 'arch' | 'gem';
  colour: number;
  /** Items each player has taken, items left, and whether all were taken (PERFECT, in the results). */
  counts: [number, number];
  left: number;
  total: number;
  perfect: boolean;
}

export interface EffectsLook {
  /** The clock's freeze is on: the C64 flips the background colours (EOR 7) for its duration. */
  clock: boolean;
  /** The yellow cross's strike: its 48x48 kill box, top-left in the VIC frame (at most one). */
  strikes: { x: number; y: number }[];
  /** The blue cross's flood: tile rows filled from the bottom (0..25); it stays for the rest of the level. */
  flood: number;
  /** Explosions: where they start (VIC frame) and 0..1 through their life. */
  explosions: { x: number; y: number; t: number }[];
  /** The potion bonus stage: seconds left, or -1 when not on. */
  bonusSeconds: number;
  /** The border (and background) flashing while play is paused: a strike starting, a blast, an umbrella or door. */
  flash: boolean;
  /** The blast's six sprites (monster-sized; `big` double size), VIC frame like a monster's x, y. */
  blastPieces: BlastPiece[];
  /** Which blast: the lamp's six flying pieces, or the bomb's pulsing grid. */
  blast: 'none' | 'lamp' | 'bomb';
  bonus: BonusLook | null;
  /** Players under the power heart (the C64's state $18): drawn flashing (`motion.flash`), twice as fast. */
  heart: [boolean, boolean];
}

const NONE: EffectsLook = {
  clock: false, strikes: [], flood: 0, explosions: [], bonusSeconds: -1, flash: false, blastPieces: [], blast: 'none',
  bonus: null, heart: [false, false],
};

export function lookOf(world: World, sys: ItemSystem | undefined): EffectsLook {
  if (!sys) return { ...NONE, clock: world.power.clock > 0 };
  const b = sys.bonus;
  const pic = b ? BONUS_PICTURES.find((p) => p.potion === b.potion) : undefined;
  const bomb = sys.blast === 'bomb';
  return {
    clock: world.power.clock > 0,
    strikes: sys.strike.on ? [strikeBox(sys)] : [],
    flood: sys.flood,
    explosions: sys.blast === 'none' ? [] : [{ x: bomb ? BOMB_X : LAMP_X, y: LAMP_Y, t: blastProgress(sys) }],
    bonusSeconds: b ? b.seconds : -1,
    flash: world.flow.frame < sys.flashUntil,
    blastPieces: blastPieces(sys),
    blast: sys.blast,
    bonus: b ? {
      phase: b.phase, picture: pic?.picture ?? 'note', colour: pic?.colour ?? 5, counts: [b.counts[0], b.counts[1]],
      left: b.left, total: b.total, perfect: b.perfect,
    } : null,
    heart: [sys.heartOn[0], sys.heartOn[1]],
  };
}
